import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { Box, Container, Paper, Typography, Button } from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { CheckCircle, Refresh } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Text, HFlex } from "shared";
import { Area } from "types";
import { batchReducer, initialState } from "./reducer";
import { BatchDropzone } from "./BatchDropzone";
import { BatchReviewStep } from "./BatchReviewStep";
import { BatchSummary } from "./BatchSummary";
import { extractPdfWithValidation } from "./extraction";
import { ExtractedFields } from "./types";
import usePostReferralPdf from "routes/other/queries/usePostReferralPdf";

export const UkReferralBatchPage: React.VFC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [state, dispatch] = useReducer(batchReducer, initialState);
  const { mutateAsync } = usePostReferralPdf();
  const isProcessingRef = useRef(false);

  const handleFilesSelected = useCallback((files: File[]) => {
    dispatch({ type: "ADD_FILES", files });
  }, []);

  useEffect(() => {
    if (state.step !== "processing") {
      isProcessingRef.current = false;
      return;
    }
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const processItems = async () => {
      for (const item of state.items) {
        if (item.status !== "pending") continue;

        dispatch({ type: "EXTRACTION_START", id: item.id });

        try {
          const result = await extractPdfWithValidation(item.file);

          dispatch({
            type: "EXTRACTION_COMPLETE",
            id: item.id,
            extracted: result.fields,
            rawText: result.rawText,
            validationIssues: result.validationIssues,
          });
        } catch (err) {
          console.error("Extraction failed for", item.file.name, err);
          dispatch({
            type: "EXTRACTION_COMPLETE",
            id: item.id,
            extracted: {},
            rawText: "",
            validationIssues: [{ field: "patient_name", type: "missing", message: "Failed to extract PDF" }],
          });
        }
      }
    };

    processItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step]);

  const handleUpdateFields = useCallback((id: string, fields: Partial<ExtractedFields>) => {
    dispatch({ type: "UPDATE_FIELDS", id, fields });
  }, []);

  const handleRemove = useCallback((id: string) => {
    dispatch({ type: "REMOVE_ITEM", id });
  }, []);

  const handleSubmitAll = useCallback(async () => {
    if (state.step !== "summary") return;

    const itemsToSubmit = state.items.filter(item => item.status === "extracted" && item.extracted);

    if (itemsToSubmit.length === 0) {
      dispatch({ type: "FINISH" });
      return;
    }

    dispatch({ type: "START_SUBMIT" });

    for (const item of itemsToSubmit) {
      const fields = item.extracted!;
      const bodyParts = fields.body_parts || [];
      const pathwayIds = (fields.pathway_ids || "").split(",").map(id => id.trim()).filter(Boolean);
      // If phone is set and not in phone_numbers, it's a custom entry - use it
      const isCustomPhone = fields.phone && !fields.phone_numbers?.includes(fields.phone);
      const phoneNumbers: string[] | undefined = isCustomPhone && fields.phone
        ? [fields.phone]
        : (fields.phone_numbers?.length ? fields.phone_numbers : (fields.phone ? [fields.phone] : undefined));

      try {
        await mutateAsync({
          uk_referral_data: {
            referral_date: fields.referral_date || "",
            site: fields.site || "",
            urgency: fields.urgency || "Routine",
            pathway_ids: pathwayIds,
            body_parts_referred: bodyParts,
            invited_joint: fields.invited_joint || bodyParts[0] || "",
            patient: {
              name: fields.patient_name || "",
              date_of_birth: fields.date_of_birth || "",
              nhs_number: fields.nhs_number || "",
              email: fields.email || undefined,
              telephone_numbers: phoneNumbers,
              address: fields.address || undefined,
            },
            gp: (fields.gp_name || fields.gp_address) ? {
              name: fields.gp_name || undefined,
              address: fields.gp_address || undefined,
            } : undefined,
          },
          file: item.file,
        });
        dispatch({ type: "SUBMIT_SUCCESS", id: item.id });
      } catch (err) {
        dispatch({ type: "SUBMIT_ERROR", id: item.id, error: String(err) });
      }
    }
  }, [state, mutateAsync]);

  const goBack = () => history.push(Area.Other);

  if (state.step === "empty") {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Text heading bold mb={3}>
            {t("form.uk_referral_batch")}
          </Text>
          <BatchDropzone onFilesSelected={handleFilesSelected} />
        </Box>
      </Container>
    );
  }

  if (state.step === "processing") {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Text heading bold mb={3}>
            {t("form.uk_referral_batch")}
          </Text>
          <BatchDropzone
            onFilesSelected={handleFilesSelected}
            items={state.items}
            isProcessing
          />
        </Box>
      </Container>
    );
  }

  if (state.step === "review") {
    return (
      <Container maxWidth="xl">
        <Box py={2}>
          <BatchReviewStep
            items={state.items}
            currentIndex={state.currentIndex}
            onUpdateFields={handleUpdateFields}
            onRemove={handleRemove}
            onNext={() => dispatch({ type: "NEXT_ITEM" })}
            onPrev={() => dispatch({ type: "PREV_ITEM" })}
            onSkipToSummary={() => dispatch({ type: "GO_TO_SUMMARY" })}
          />
        </Box>
      </Container>
    );
  }

  if (state.step === "summary" || state.step === "submitting") {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <BatchSummary
            items={state.items}
            onRemove={handleRemove}
            onEditItem={(index) => dispatch({ type: "GO_TO_ITEM", index })}
            onBack={() => dispatch({ type: "START_REVIEW" })}
            onSubmit={handleSubmitAll}
            isSubmitting={state.step === "submitting"}
            submittedCount={state.step === "submitting" ? state.submittedCount : 0}
            totalToSubmit={state.step === "submitting" ? state.totalToSubmit : 0}
          />
        </Box>
      </Container>
    );
  }

  if (state.step === "success") {
    return (
      <Container maxWidth="sm">
        <Paper elevation={2}>
          <Box p={4} textAlign="center">
            <CheckCircle style={{ fontSize: 64, color: green[500], marginBottom: 16 }} />
            <Text heading bold mb={2}>
              {t("form.batch_complete")}
            </Text>
            <Typography variant="body1" gutterBottom>
              {t("form.batch_results", {
                submitted: state.submitted,
                referralWord: state.submitted === 1 ? "referral" : "referrals",
                failed: state.failed,
              })}
            </Typography>
            {state.submitted > 0 && (
              <Typography variant="body2" color="textSecondary">
                {t("form.batch_invites_sent")}
              </Typography>
            )}
            <Box mt={3}>
              <HFlex justifyContent="center" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => dispatch({ type: "RESET" })}
                >
                  {t("buttons.upload_more")}
                </Button>
                <Button variant="contained" color="primary" onClick={goBack}>
                  {t("buttons.done")}
                </Button>
              </HFlex>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return null;
};
